// pages/CustomWorkers/CustomWorkers.jsx
//
// Brand-facing marketplace for bespoke ("custom") workers. A company admin
// submits a spec, the super-admin quotes a recurring price, the two sides
// negotiate, and once agreed a Stripe Checkout link appears here to pay. After
// payment the worker shows "active" with a countdown to its availability date.
// Backend: /api/custom-worker-requests (see routes/customWorkerMarketplace.js).

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { get, post } from "../../api";

const PERIODS = ["monthly", "yearly"];
const STATUS_LABEL = {
  pending: "Awaiting quote",
  quoted: "Quote received",
  countered: "Counter sent",
  accepted: "Agreed",
  awaiting_payment: "Awaiting payment",
  active: "Active",
  rejected: "Declined",
  cancelled: "Cancelled",
};
const STATUS_COLOR = {
  pending: "#6b7280", quoted: "#2563eb", countered: "#d97706", accepted: "#059669",
  awaiting_payment: "#7c3aed", active: "#059669", rejected: "#dc2626", cancelled: "#9ca3af",
};

const BLANK = {
  title: "", jobDescription: "", inputDescription: "", outputDescription: "",
  exampleInput: "", exampleOutput: "", deliveryExplanation: "", steps: "",
};

function money(p, period) { return `$${Number(p || 0).toLocaleString()}/${period || "monthly"}`; }
function fmtDate(d) { return d ? new Date(d).toDateString() : "—"; }
function daysUntil(d) {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 86400000);
}
// The brand can accept only the super-admin's STANDING offer.
function canAcceptQuote(r) { return r.status === "quoted" && r.lastOfferBy === "super"; }
function canCancel(r) { return !["active", "rejected", "cancelled"].includes(r.status); }

export default function CustomWorkers() {
  const userInfo = useSelector((s) => s.user.userInfo);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [counterFor, setCounterFor] = useState(null); // request id being countered
  const [counter, setCounter] = useState({ price: "", period: "monthly", startDate: "", note: "" });

  const fetchList = useCallback(async () => {
    try {
      const r = await get("/api/custom-worker-requests");
      setRows((r && r.requests) || []);
      setErr(null);
    } catch (e) { setErr((e && e.message) || "Failed to load requests"); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitRequest = async () => {
    if (!form.title.trim()) return setErr("Title is required");
    setBusy(true); setErr(null);
    try {
      const body = { ...form, steps: form.steps.split("\n").map((s) => s.trim()).filter(Boolean) };
      await post("/api/custom-worker-requests", body, false);
      setShowForm(false); setForm(BLANK); setMsg("Request sent to the Prigmar team.");
      await fetchList();
    } catch (e) { setErr((e && e.message) || "Could not submit request"); }
    finally { setBusy(false); }
  };

  const act = async (id, path, body) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await post(`/api/custom-worker-requests/${id}/${path}`, body || {}, false);
      if (r && r.checkoutUrl) { setMsg("Opening secure payment…"); window.open(r.checkoutUrl, "_blank"); }
      setCounterFor(null);
      await fetchList();
      return r;
    } catch (e) { setErr((e && e.message) || "Action failed"); }
    finally { setBusy(false); }
  };

  const pay = async (id, existingUrl) => {
    if (existingUrl) return window.open(existingUrl, "_blank");
    try {
      const r = await get(`/api/custom-worker-requests/${id}/payment`);
      if (r && r.checkoutUrl) window.open(r.checkoutUrl, "_blank");
      else setErr("No payment link yet — please retry shortly.");
    } catch (e) { setErr((e && e.message) || "Could not fetch payment link"); }
  };

  const submitCounter = (id) => {
    if (!(Number(counter.price) > 0)) return setErr("Enter a price greater than 0");
    if (!counter.startDate) return setErr("Pick a proposed start date");
    return act(id, "counter", counter);
  };

  return (
    <div className="container py-4">
      <style>{`
        .cw-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
        .cw-card { border:1px solid #e5e7eb; border-radius:10px; padding:16px; margin-bottom:14px; background:#fff; }
        .cw-badge { color:#fff; border-radius:999px; padding:2px 10px; font-size:12px; font-weight:600; }
        .cw-btn { border:none; border-radius:8px; padding:7px 14px; font-weight:600; cursor:pointer; margin-right:8px; }
        .cw-btn.primary { background:#2563eb; color:#fff; } .cw-btn.green { background:#059669; color:#fff; }
        .cw-btn.ghost { background:#f3f4f6; color:#374151; } .cw-btn.danger { background:#fee2e2; color:#b91c1c; }
        .cw-btn:disabled { opacity:.5; cursor:not-allowed; }
        .cw-field { display:block; width:100%; margin:6px 0 12px; padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; }
        .cw-label { font-size:13px; font-weight:600; color:#374151; }
        .cw-terms { font-size:14px; color:#111827; margin:6px 0; }
        .cw-offers { font-size:12px; color:#6b7280; margin-top:8px; }
        .cw-row { display:flex; gap:12px; flex-wrap:wrap; }
        .cw-row > div { flex:1; min-width:160px; }
      `}</style>

      <div className="cw-head">
        <div>
          <h3 style={{ margin: 0 }}>Custom Workers</h3>
          <div style={{ color: "#6b7280", fontSize: 13 }}>Request a bespoke AI worker tailored to your workflow.</div>
        </div>
        <button className="cw-btn primary" onClick={() => { setShowForm((v) => !v); setErr(null); }}>
          {showForm ? "Close" : "+ Request a worker"}
        </button>
      </div>

      {err && <div className="alert alert-warning">{err}</div>}
      {msg && <div className="alert alert-success">{msg}</div>}

      {showForm && (
        <div className="cw-card">
          <span className="cw-label">Title *</span>
          <input className="cw-field" value={form.title} onChange={(e) => setF("title", e.target.value)} placeholder="e.g. Compare contractor bids" />
          <span className="cw-label">What is the job?</span>
          <textarea className="cw-field" rows={2} value={form.jobDescription} onChange={(e) => setF("jobDescription", e.target.value)} />
          <div className="cw-row">
            <div><span className="cw-label">What is the input?</span>
              <textarea className="cw-field" rows={2} value={form.inputDescription} onChange={(e) => setF("inputDescription", e.target.value)} /></div>
            <div><span className="cw-label">What is the output?</span>
              <textarea className="cw-field" rows={2} value={form.outputDescription} onChange={(e) => setF("outputDescription", e.target.value)} /></div>
          </div>
          <div className="cw-row">
            <div><span className="cw-label">Example input (optional)</span>
              <textarea className="cw-field" rows={2} value={form.exampleInput} onChange={(e) => setF("exampleInput", e.target.value)} /></div>
            <div><span className="cw-label">Example output (optional)</span>
              <textarea className="cw-field" rows={2} value={form.exampleOutput} onChange={(e) => setF("exampleOutput", e.target.value)} /></div>
          </div>
          <span className="cw-label">How should the worker deliver the work?</span>
          <textarea className="cw-field" rows={2} value={form.deliveryExplanation} onChange={(e) => setF("deliveryExplanation", e.target.value)} />
          <span className="cw-label">Steps to achieve the work (one per line)</span>
          <textarea className="cw-field" rows={4} value={form.steps} onChange={(e) => setF("steps", e.target.value)} placeholder={"Receive document\nExtract fields\nReturn mapping"} />
          <button className="cw-btn green" disabled={busy} onClick={submitRequest}>{busy ? "Submitting…" : "Submit request"}</button>
        </div>
      )}

      {rows.length === 0 && !showForm && <div style={{ color: "#6b7280" }}>No custom worker requests yet.</div>}

      {rows.map((r) => {
        const supersOffer = canAcceptQuote(r);
        const dleft = daysUntil(r.availabilityDate);
        return (
          <div className="cw-card" key={r._id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{r.title}</strong>
              <span className="cw-badge" style={{ background: STATUS_COLOR[r.status] || "#6b7280" }}>{STATUS_LABEL[r.status] || r.status}</span>
            </div>
            {r.jobDescription && <div style={{ color: "#6b7280", fontSize: 13, margin: "4px 0" }}>{r.jobDescription}</div>}

            {(r.price > 0) && <div className="cw-terms">Terms: <strong>{money(r.price, r.period)}</strong> · start {fmtDate(r.startDate)} {r.lastOfferBy ? `· last offer by ${r.lastOfferBy === "super" ? "Prigmar" : "you"}` : ""}</div>}

            {r.status === "active" && (
              <div className="cw-terms">
                {dleft > 0 ? <>Active — available in <strong>{dleft} day{dleft === 1 ? "" : "s"}</strong> ({fmtDate(r.availabilityDate)}).</>
                  : <>✅ Live and available.</>}
              </div>
            )}
            {r.status === "rejected" && r.rejectedReason && <div className="cw-terms" style={{ color: "#b91c1c" }}>Reason: {r.rejectedReason}</div>}

            <div style={{ marginTop: 10 }}>
              {supersOffer && <>
                <button className="cw-btn green" disabled={busy} onClick={() => act(r._id, "accept")}>Accept &amp; pay</button>
                <button className="cw-btn ghost" disabled={busy} onClick={() => { setCounterFor(counterFor === r._id ? null : r._id); setCounter({ price: r.price || "", period: r.period || "monthly", startDate: "", note: "" }); }}>Counter-offer</button>
              </>}
              {r.status === "awaiting_payment" && <button className="cw-btn primary" disabled={busy} onClick={() => pay(r._id, r.stripeCheckoutUrl)}>Pay now</button>}
              {canCancel(r) &&
                <button className="cw-btn danger" disabled={busy} onClick={() => act(r._id, "cancel")}>Cancel</button>}
            </div>

            {counterFor === r._id && (
              <div className="cw-card" style={{ marginTop: 10, background: "#f9fafb" }}>
                <div className="cw-row">
                  <div><span className="cw-label">Your price (USD)</span>
                    <input type="number" className="cw-field" value={counter.price} onChange={(e) => { const _v = e.target.value; setCounter((c) => ({ ...c, price: _v })); }} /></div>
                  <div><span className="cw-label">Period</span>
                    <select className="cw-field" value={counter.period} onChange={(e) => { const _v = e.target.value; setCounter((c) => ({ ...c, period: _v })); }}>
                      {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select></div>
                  <div><span className="cw-label">Proposed start</span>
                    <input type="date" className="cw-field" value={counter.startDate} onChange={(e) => { const _v = e.target.value; setCounter((c) => ({ ...c, startDate: _v })); }} /></div>
                </div>
                <span className="cw-label">Note (optional)</span>
                <input className="cw-field" value={counter.note} onChange={(e) => { const _v = e.target.value; setCounter((c) => ({ ...c, note: _v })); }} />
                <button className="cw-btn primary" disabled={busy} onClick={() => submitCounter(r._id)}>Send counter-offer</button>
              </div>
            )}

            {Array.isArray(r.offers) && r.offers.length > 0 && (
              <div className="cw-offers">
                {r.offers.map((o, i) => <div key={i}>• {o.by === "super" ? "Prigmar" : "You"} offered {money(o.price, o.period)} starting {fmtDate(o.startDate)}{o.note ? ` — ${o.note}` : ""}</div>)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

CustomWorkers._test = { money, fmtDate, daysUntil, canAcceptQuote, canCancel, STATUS_LABEL };
